// Builds the 3-level Group -> Folder -> Layer hierarchy the Scene Manager
// panel renders (layout/interaction pattern only, modeled on a reference
// UI/UX the user shared — no borrowed branding, see SceneManager.tsx).
// This REPLACES the earlier flat layer list. Structure is derived from the
// same `LayerEntry[]` the map itself uses (src/map/MapContext.tsx) —
// nothing here owns a separate copy of layer state, it only groups
// references to it.
import { isLayerAvailable, type LayerEntry, type MeasurementEntity } from './MapContext';
import type { ApiFolder } from '../api/workspaceFolders';

export interface SceneLayerNode {
  type: 'layer';
  id: string;
  title: string;
  entry: LayerEntry;
}

/** A single persisted, user-drawn measurement — unlike SceneLayerNode
 * (which wraps a whole OL layer, e.g. all Field Photos pins together), each
 * measurement gets its OWN row, since each one is independently named,
 * toggleable and clickable-to-detail (see the plan's "Workspace content"
 * spec). */
export interface SceneMeasurementNode {
  type: 'measurement';
  id: string;
  title: string;
  measurement: MeasurementEntity;
}

export interface SceneContainerNode {
  type: 'group' | 'folder';
  id: string;
  title: string;
  children: SceneNode[];
  /** Set on the folders the user made (platform_db workspace_folders) -- those can be renamed and
   * deleted, unlike the built-in folders. */
  userFolderId?: string;
}

export type SceneNode = SceneContainerNode | SceneLayerNode | SceneMeasurementNode;

function layerNode(entry: LayerEntry): SceneLayerNode {
  return { type: 'layer', id: entry.id, title: entry.title, entry };
}

function measurementNode(m: MeasurementEntity): SceneMeasurementNode {
  return { type: 'measurement', id: `measurement:${m.id}`, title: m.name, measurement: m };
}

export function buildSceneTree(
  layers: LayerEntry[],
  measurements: MeasurementEntity[] = [],
  folders: ApiFolder[] = [],
  workspaceSlug: string | null = null,
): SceneContainerNode[] {
  const basemap = layers.filter((l) => l.kind === 'basemap');
  const rasters = layers.filter((l) => l.kind === 'raster');
  const vectors = layers.filter((l) => l.kind === 'vector');
  const photos = layers.filter((l) => l.kind === 'photo');

  const tree: SceneContainerNode[] = [];

  // Workspace content goes first — the user's own, most actively-changing content for THIS
  // workspace (every measurement and folder belongs to exactly one workspace), ahead of the
  // read-only GeoServer/Field Photos/Basemap groups. Always present, even when empty, so there is
  // somewhere to add the first folder.
  const content: SceneContainerNode[] = folders.map((f) => ({
    type: 'folder' as const,
    id: `folder:user:${f.folder_id}`,
    title: f.name,
    userFolderId: f.folder_id,
    children: measurements.filter((m) => m.folderId === f.folder_id).map(measurementNode),
  }));
  const knownFolderIds = new Set(folders.map((f) => f.folder_id));
  // Un-filed shapes (and any whose folder isn't known) -- shown when there are some, or when the
  // workspace has no folders yet so a first drawing has a visible home.
  const unfiled = measurements.filter((m) => !m.folderId || !knownFolderIds.has(m.folderId));
  if (unfiled.length || folders.length === 0) {
    content.push({
      type: 'folder',
      id: 'folder:measurements',
      title: 'Measurements',
      children: unfiled.map(measurementNode),
    });
  }
  tree.push({ type: 'group', id: 'group:workspace-content', title: 'Workspace content', children: content });

  if (rasters.length || vectors.length) {
    const children: SceneContainerNode[] = [];
    if (rasters.length) {
      children.push({
        type: 'folder',
        id: 'folder:imagery',
        title: 'Imagery',
        children: rasters.map(layerNode),
      });
    }
    if (vectors.length) {
      children.push({
        type: 'folder',
        id: 'folder:survey',
        title: 'Survey',
        children: vectors.map(layerNode),
      });
    }
    tree.push({ type: 'group', id: 'group:geoserver', title: 'GeoServer (abrar)', children });
  }

  // Field Photos pins belong to the Field Photos workspace only.
  if (photos.length && workspaceSlug === 'field-photos') {
    tree.push({
      type: 'group',
      id: 'group:field-photos',
      title: 'Field Photos',
      children: [
        {
          type: 'folder',
          id: 'folder:field-photos',
          title: 'Field Photos',
          children: photos.map(layerNode),
        },
      ],
    });
  }

  if (basemap.length) {
    tree.push({
      type: 'group',
      id: 'group:basemap',
      title: 'Basemap',
      children: [
        {
          type: 'folder',
          id: 'folder:base-layers',
          title: 'Base Layers',
          children: basemap.map(layerNode),
        },
      ],
    });
  }

  return tree;
}

export type VisibilityState = 'all' | 'none' | 'mixed';

function isContainer(node: SceneNode): node is SceneContainerNode {
  return node.type === 'group' || node.type === 'folder';
}

/** A layer GeoServer doesn't have (or can't serve) right now — listed in
 * the tree with an inline marker, but not toggleable and not counted when
 * working out whether a folder/group is "all"/"none"/"mixed" visible. */
export function isLeafUnavailable(node: SceneLayerNode | SceneMeasurementNode): boolean {
  return node.type === 'layer' && !isLayerAvailable(node.entry);
}

/** A raster the user hasn't touched that the timeline is currently hiding -- not "off", just
 * "not this date". Like an unavailable leaf, it doesn't count toward a folder's all/none/mixed
 * state, so a folder with the visible satellite image and an out-of-date raster reads "all". */
function isTimelineHidden(node: SceneLayerNode | SceneMeasurementNode): boolean {
  return node.type === 'layer' && node.entry.kind === 'raster' && node.entry.userVisible === undefined && !node.entry.layer.getVisible();
}

/** True if the individual leaf itself (layer or measurement) is visible. */
function leafVisible(node: SceneLayerNode | SceneMeasurementNode): boolean {
  return node.type === 'layer' ? node.entry.layer.getVisible() : node.measurement.visible;
}

/**
 * Aggregate visibility of every leaf under `node` (or of `node` itself, if
 * it's a leaf) — drives a group/folder's own "eye" icon. Three states, not
 * two: 'all' (every leaf visible), 'none' (every leaf hidden), 'mixed'
 * (some visible, some not). Collapsing 'mixed' into a plain boolean was the
 * bug: a container whose leaves are NOT all visible was rendered with the
 * same fully-crossed-out eye as one that's entirely hidden, which reads as
 * "the whole group is off" even while most of its content is still
 * rendering on the map. See SceneManager.tsx's EyeIcon/EyeMixedIcon usage.
 */
export function nodeVisibilityState(node: SceneNode): VisibilityState {
  if (node.type === 'layer' || node.type === 'measurement') {
    return leafVisible(node) ? 'all' : 'none';
  }
  // Unavailable leaves don't count either way — a folder whose only
  // hidden entries are unpublished rasters is still fully "on".
  const counted = node.children.filter((c) => isContainer(c) || (!isLeafUnavailable(c) && !isTimelineHidden(c)));
  if (counted.length === 0) return 'all';
  const states = counted.map(nodeVisibilityState);
  const allVisible = states.every((s) => s === 'all');
  const allHidden = states.every((s) => s === 'none');
  if (allVisible) return 'all';
  if (allHidden) return 'none';
  return 'mixed';
}

/** Visits every leaf (layer or measurement) under `node`, recursively. Used
 * for a group/folder's "toggle everything under me" eye button. */
export function forEachLeaf(node: SceneNode, fn: (leaf: SceneLayerNode | SceneMeasurementNode) => void): void {
  if (node.type === 'layer' || node.type === 'measurement') {
    fn(node);
    return;
  }
  node.children.forEach((c) => forEachLeaf(c, fn));
}
